/**
 * LOGOS Session Tracker — Panel de Seguimiento Longitudinal
 * 
 * Visualiza el progreso del usuario a través de sesiones:
 * - Línea temporal de Λ
 * - Tabla de sesiones
 * - Resumen semanal
 * - Heatmap por día de la semana
 * - Evolución por dominio (radar comparativo)
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, ReferenceLine,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  BarChart, Bar, Cell,
} from 'recharts';
import { COLORS, FONTS } from '../domain';

const fonts = FONTS;

// ═══ SESSION STORAGE MANAGER ═══
const SESSION_KEY = 'logos-session-history';
const DETAIL_KEY = 'logos-session-details';

export function saveSessionSnapshot(metrics, state, domains) {
  try {
    const snapshot = {
      id: Date.now(),
      date: new Date().toISOString(),
      lambda: metrics.lambda,
      omega: metrics.omega,
      V: metrics.V,
      S: metrics.S,
      C: metrics.C,
      F: metrics.F,
      Q: metrics.Q,
      verdict: metrics.verdict,
      domains: domains ? { ...domains } : null,
      state: state ? { ...state } : null,
    };
    const prev = JSON.parse(localStorage.getItem(SESSION_KEY) || '[]');
    const updated = [...prev, snapshot].slice(-100);
    localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
    return snapshot;
  } catch { return null; }
}

export function getSessionHistory() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || '[]');
  } catch { return []; }
}

export function startSession() {
  const sessionStart = {
    startTime: new Date().toISOString(),
    id: Date.now(),
  };
  sessionStorage.setItem('logos-current-session', JSON.stringify(sessionStart));
  return sessionStart;
}

export function endSession(metrics, state, domains) {
  try {
    const current = JSON.parse(sessionStorage.getItem('logos-current-session') || '{}');
    const startTime = current.startTime ? new Date(current.startTime) : new Date();
    const endTime = new Date();
    const durationMin = Math.round((endTime - startTime) / 60000);

    const detail = {
      id: current.id || Date.now(),
      startTime: current.startTime || endTime.toISOString(),
      endTime: endTime.toISOString(),
      durationMin,
      lambda: metrics.lambda,
      omega: metrics.omega,
      V: metrics.V,
      S: metrics.S,
      C: metrics.C,
      F: metrics.F,
      Q: metrics.Q,
      verdict: metrics.verdict,
      domains: domains ? { ...domains } : null,
    };

    const prev = JSON.parse(localStorage.getItem(DETAIL_KEY) || '[]');
    const updated = [...prev, detail].slice(-100);
    localStorage.setItem(DETAIL_KEY, JSON.stringify(updated));

    saveSessionSnapshot(metrics, state, domains);
    sessionStorage.removeItem('logos-current-session');
    return detail;
  } catch { return null; }
}

export function getDetailedSessions() {
  try {
    return JSON.parse(localStorage.getItem(DETAIL_KEY) || '[]');
  } catch { return []; }
}

// ═══ MAIN COMPONENT ═══
export default function SessionTracker({ currentMetrics, currentDomains }) {
  const { t, i18n } = useTranslation();
  const [subTab, setSubTab] = useState('timeline');
  const [dbSessions, setDbSessions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch sessions from Supabase, fallback to localStorage
  useEffect(() => {
    import('../services/supabase').then(({ supabase }) => {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) {
          supabase
            .from('sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('start_time', { ascending: false })
            .limit(200)
            .then(({ data }) => {
              if (data?.length > 0) {
                setDbSessions(data.reverse()); // Supabase returns DESC, we want ASC
              }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        } else {
          setLoading(false);
        }
      });
    }).catch(() => setLoading(false));
  }, []);

  const localSessions = useMemo(() => getDetailedSessions(), []);
  const localSnapshots = useMemo(() => getSessionHistory(), []);

  const allData = useMemo(() => {
    // Prefer DB data, fallback to localStorage
    let combined;
    if (dbSessions && dbSessions.length > 0) {
      combined = dbSessions.map(s => ({
        ...s,
        lambda: parseFloat(s.lambda),
        omega: parseFloat(s.omega),
        V: parseFloat(s.viability),
        S: parseFloat(s.entropy),
        C: parseFloat(s.coherence),
        F: parseFloat(s.free_energy),
        Q: parseFloat(s.trajectory),
        startTime: s.start_time,
        durationMin: s.duration_min,
      }));
    } else {
      combined = localSessions.length > 0 ? localSessions : localSnapshots;
    }
    return combined.map((s, i) => {
      const d = new Date(s.startTime || s.start_time || s.date);
      const locale = i18n.language === 'en' ? 'en-US' : 'es-MX';
      const dayNames = t('tracker.dayNames', { returnObjects: true });
      return {
        ...s,
        idx: i + 1,
        dateLabel: d.toLocaleDateString(locale, { day: '2-digit', month: 'short' }),
        timeLabel: d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
        dayName: Array.isArray(dayNames) ? dayNames[d.getDay()] : ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'][d.getDay()],
        dayIdx: d.getDay(),
      };
    });
  }, [dbSessions, localSessions, localSnapshots]);

  // ═══ COMPUTED ANALYTICS ═══
  const analytics = useMemo(() => {
    if (allData.length === 0) return null;

    const lambdas = allData.map(s => s.lambda);
    const avgLambda = lambdas.reduce((a, b) => a + b, 0) / lambdas.length;
    const recent5 = lambdas.slice(-5);
    const first5 = lambdas.slice(0, Math.min(5, lambdas.length));
    const recentAvg = recent5.reduce((a, b) => a + b, 0) / recent5.length;
    const firstAvg = first5.reduce((a, b) => a + b, 0) / first5.length;
    const trend = recentAvg - firstAvg;

    // Day of week analysis
    const dayMap = {};
    const dn = t('tracker.dayNames', { returnObjects: true });
    const dayNamesArr = Array.isArray(dn) ? dn : ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    allData.forEach(s => {
      if (!dayMap[s.dayIdx]) dayMap[s.dayIdx] = [];
      dayMap[s.dayIdx].push(s.lambda);
    });
    const dayAverages = dayNamesArr.map((name, idx) => {
      const vals = dayMap[idx] || [];
      return {
        day: name,
        avgLambda: vals.length > 0 ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(3) : 0,
        sessions: vals.length,
      };
    });
    const bestDay = dayAverages.filter(d => d.sessions > 0).sort((a, b) => b.avgLambda - a.avgLambda)[0];
    const worstDay = dayAverages.filter(d => d.sessions > 0).sort((a, b) => a.avgLambda - b.avgLambda)[0];

    // Streak calculation
    const dates = [...new Set(allData.map(s => new Date(s.startTime || s.date).toDateString()))];
    let streak = 0;
    const today = new Date();
    for (let i = 0; i < 60; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      if (dates.includes(checkDate.toDateString())) {
        streak++;
      } else if (i > 0) break;
    }

    // Weekly data
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisWeek = allData.filter(s => new Date(s.startTime || s.date) >= oneWeekAgo);
    const weekAvgLambda = thisWeek.length > 0
      ? thisWeek.reduce((a, s) => a + s.lambda, 0) / thisWeek.length
      : 0;

    // Verdict distribution
    const verdicts = {};
    allData.forEach(s => {
      if (s.verdict) verdicts[s.verdict] = (verdicts[s.verdict] || 0) + 1;
    });

    // Time in A+ vs A-
    const inAPlus = allData.filter(s => s.lambda >= 0.45).length;
    const inAMinus = allData.filter(s => s.lambda < 0.20).length;

    return {
      total: allData.length,
      avgLambda: +avgLambda.toFixed(3),
      trend: trend > 0.03 ? t('tracker.trendUp') : trend < -0.03 ? t('tracker.trendDown') : t('tracker.trendStable'),
      trendDelta: +trend.toFixed(3),
      dayAverages,
      bestDay,
      worstDay,
      streak,
      thisWeekSessions: thisWeek.length,
      weekAvgLambda: +weekAvgLambda.toFixed(3),
      verdicts,
      pctAPlus: +((inAPlus / allData.length) * 100).toFixed(1),
      pctAMinus: +((inAMinus / allData.length) * 100).toFixed(1),
      maxLambda: +Math.max(...lambdas).toFixed(3),
      minLambda: +Math.min(...lambdas).toFixed(3),
    };
  }, [allData]);

  // ═══ LOADING STATE ═══
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}` }}>
        <div style={{ fontSize: 28, marginBottom: 12, animation: 'breathe 2s ease infinite' }}>📊</div>
        <div style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('tracker.loading')}</div>
      </div>
    );
  }

  // ═══ EMPTY STATE ═══
  if (allData.length === 0) {
    return (
      <div style={{
        textAlign: 'center', padding: 60,
        background: COLORS.surface, borderRadius: 14,
        border: `1px solid ${COLORS.border}`,
      }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>📊</div>
        <div style={{ fontSize: 16, fontFamily: fonts.display, color: COLORS.text, marginBottom: 8 }}>
          {t('tracker.emptyTitle')}
        </div>
        <div style={{ fontSize: 12, fontFamily: fonts.mono, color: COLORS.textMuted, lineHeight: 1.8, maxWidth: 400, margin: '0 auto' }}>
          {t('tracker.emptyDesc')}
        </div>
        {currentMetrics && (
          <div style={{
            marginTop: 24, padding: 16, background: `${COLORS.logos}08`,
            borderRadius: 10, border: `1px solid ${COLORS.logos}20`,
            display: 'inline-block',
          }}>
            <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 1, marginBottom: 4 }}>
              {t('tracker.currentState')}
            </div>
            <div style={{ fontSize: 28, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 700 }}>
              Λ = {currentMetrics.lambda?.toFixed(3)}
            </div>
            <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 4 }}>
              {t('tracker.savedOnClose')}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══ SUB-TAB NAVIGATION ═══
  const subTabs = [
    { id: 'timeline', label: t('tracker.tabTimeline'), icon: '📈' },
    { id: 'sessions', label: t('tracker.tabSessions'), icon: '📋' },
    { id: 'weekly', label: t('tracker.tabWeekly'), icon: '📊' },
    { id: 'patterns', label: t('tracker.tabPatterns'), icon: '🔍' },
  ];

  const trendColor = analytics.trendDelta > 0.03 ? COLORS.viability
    : analytics.trendDelta < -0.03 ? COLORS.entropy : COLORS.freeEnergy;

  return (
    <div style={{ animation: 'fadeInUp 0.5s ease' }}>
      {/* SUMMARY CARDS */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 10, marginBottom: 20,
      }}>
        <SummaryCard label={t('tracker.sessions')} value={analytics.total} color={COLORS.logos} />
        <SummaryCard label={t('tracker.avgLambda')} value={analytics.avgLambda} color={COLORS.logos} />
        <SummaryCard label={t('tracker.trend')} value={analytics.trend} color={trendColor} sub={`Δ ${analytics.trendDelta > 0 ? '+' : ''}${analytics.trendDelta}`} />
        <SummaryCard label={t('tracker.streak')} value={`${analytics.streak}d`} color={analytics.streak >= 7 ? COLORS.gold : analytics.streak >= 3 ? COLORS.viability : COLORS.textMuted} />
        <SummaryCard label={t('tracker.pctAPlus')} value={`${analytics.pctAPlus}%`} color={COLORS.viability} />
        <SummaryCard label={t('tracker.maxLambda')} value={analytics.maxLambda} color={COLORS.gold} />
      </div>

      {/* SUB-TAB NAV */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {subTabs.map(tab => (
          <button key={tab.id} onClick={() => setSubTab(tab.id)} style={{
            padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, transition: 'all 0.3s',
            background: subTab === tab.id ? `${COLORS.logos}25` : 'transparent',
            color: subTab === tab.id ? COLORS.text : COLORS.textMuted,
            borderBottom: subTab === tab.id ? `2px solid ${COLORS.logos}` : '2px solid transparent',
          }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ TIMELINE TAB ═══ */}
      {subTab === 'timeline' && (
        <div style={{ background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20 }}>
          <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 2, marginBottom: 16, fontWeight: 600 }}>
            {t('tracker.lambdaEvolution')}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={allData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="lambdaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.logos} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.logos} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 9, fill: COLORS.textMuted, fontFamily: fonts.mono }} />
              <YAxis domain={[0, 1]} tick={{ fontSize: 9, fill: COLORS.textMuted, fontFamily: fonts.mono }} />
              <Tooltip
                contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 11, fontFamily: fonts.mono }}
                labelStyle={{ color: COLORS.text }}
              />
              <ReferenceLine y={0.45} stroke={COLORS.viability} strokeDasharray="5 5" label={{ value: 'A⁺', fill: COLORS.viability, fontSize: 10 }} />
              <ReferenceLine y={0.20} stroke={COLORS.entropy} strokeDasharray="5 5" label={{ value: 'A⁻', fill: COLORS.entropy, fontSize: 10 }} />
              <Area type="monotone" dataKey="lambda" stroke={COLORS.logos} fill="url(#lambdaGrad)" strokeWidth={2} dot={{ r: 3, fill: COLORS.logos }} name="Λ" />
              <Line type="monotone" dataKey="omega" stroke={COLORS.gold} strokeWidth={1} dot={false} name="Ω" strokeDasharray="4 4" />
            </AreaChart>
          </ResponsiveContainer>

          {/* Secondary metrics */}
          <div style={{ marginTop: 16, fontSize: 11, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1, marginBottom: 8 }}>
            V · S · C
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={allData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 8, fill: COLORS.textMuted }} />
              <YAxis domain={[0, 1]} tick={{ fontSize: 8, fill: COLORS.textMuted }} />
              <Line type="monotone" dataKey="V" stroke={COLORS.viability} strokeWidth={1.5} dot={false} name="V" />
              <Line type="monotone" dataKey="S" stroke={COLORS.entropy} strokeWidth={1.5} dot={false} name="S" />
              <Line type="monotone" dataKey="C" stroke={COLORS.coherence || '#a78bfa'} strokeWidth={1.5} dot={false} name="C" />
              <Tooltip contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 10, fontFamily: fonts.mono }} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: fonts.mono }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ═══ SESSIONS TABLE TAB ═══ */}
      {subTab === 'sessions' && (
        <div style={{ background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20, overflowX: 'auto' }}>
          <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 2, marginBottom: 16, fontWeight: 600 }}>
            {t('tracker.sessionHistory', { count: allData.length })}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: fonts.mono, fontSize: 10 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {[t('tracker.colNum'), t('tracker.colDate'), t('tracker.colTime'), t('tracker.colDuration'), 'Λ', 'Ω', 'V', 'S', 'C', t('tracker.colVerdict')].map(h => (
                  <th key={h} style={{ padding: '8px 6px', textAlign: 'left', color: COLORS.textMuted, fontSize: 9, letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...allData].reverse().slice(0, 30).map((s, i) => {
                const lambdaColor = s.lambda >= 0.45 ? COLORS.viability : s.lambda < 0.20 ? COLORS.entropy : COLORS.freeEnergy;
                return (
                  <tr key={s.id || i} style={{
                    borderBottom: `1px solid ${COLORS.border}`,
                    background: i % 2 === 0 ? 'transparent' : `${COLORS.surface2 || COLORS.border}20`,
                  }}>
                    <td style={{ padding: '6px', color: COLORS.textMuted }}>{allData.length - i}</td>
                    <td style={{ padding: '6px', color: COLORS.text }}>{s.dateLabel}</td>
                    <td style={{ padding: '6px', color: COLORS.textMuted }}>{s.timeLabel}</td>
                    <td style={{ padding: '6px', color: COLORS.textMuted }}>{s.durationMin ? `${s.durationMin}m` : '—'}</td>
                    <td style={{ padding: '6px', color: lambdaColor, fontWeight: 700 }}>{s.lambda?.toFixed(3)}</td>
                    <td style={{ padding: '6px', color: COLORS.gold }}>{s.omega?.toFixed(3)}</td>
                    <td style={{ padding: '6px', color: COLORS.viability }}>{s.V?.toFixed(2)}</td>
                    <td style={{ padding: '6px', color: COLORS.entropy }}>{s.S?.toFixed(2)}</td>
                    <td style={{ padding: '6px', color: '#a78bfa' }}>{s.C?.toFixed(2)}</td>
                    <td style={{ padding: '6px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 9,
                        background: getVerdictColor(s.verdict) + '20',
                        color: getVerdictColor(s.verdict),
                        fontWeight: 600,
                      }}>
                        {s.verdict || '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {allData.length > 30 && (
            <div style={{ textAlign: 'center', padding: 8, fontSize: 10, color: COLORS.textMuted, fontFamily: fonts.mono }}>
              {t('tracker.showingLast', { total: allData.length })}
            </div>
          )}
        </div>
      )}

      {/* ═══ WEEKLY SUMMARY TAB ═══ */}
      {subTab === 'weekly' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {/* This week */}
          <div style={{ background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20 }}>
            <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 2, marginBottom: 16, fontWeight: 600 }}>
              {t('tracker.thisWeek')}
            </div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 48, fontFamily: fonts.mono, color: COLORS.logos, fontWeight: 700 }}>
                {analytics.weekAvgLambda}
              </div>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted }}>{t('tracker.weeklyAvgLambda')}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <MiniStat label={t('tracker.miniSessions')} value={analytics.thisWeekSessions} />
              <MiniStat label={t('tracker.miniStreak')} value={t('tracker.miniStreakDays', { n: analytics.streak })} />
              <MiniStat label={t('tracker.miniBestDay')} value={analytics.bestDay?.day || '—'} sub={`Λ ${analytics.bestDay?.avgLambda || ''}`} />
              <MiniStat label={t('tracker.miniWorstDay')} value={analytics.worstDay?.day || '—'} sub={`Λ ${analytics.worstDay?.avgLambda || ''}`} />
            </div>
          </div>

          {/* Verdict distribution */}
          <div style={{ background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20 }}>
            <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 2, marginBottom: 16, fontWeight: 600 }}>
              {t('tracker.verdictDist')}
            </div>
            {Object.entries(analytics.verdicts).sort((a, b) => b[1] - a[1]).map(([verdict, count]) => {
              const pct = ((count / analytics.total) * 100).toFixed(0);
              const color = getVerdictColor(verdict);
              return (
                <div key={verdict} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontFamily: fonts.mono, color }}>{verdict}</span>
                    <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{ width: '100%', height: 6, background: COLORS.surface2 || COLORS.border, borderRadius: 3 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.5s' }} />
                  </div>
                </div>
              );
            })}

            <div style={{ marginTop: 20, padding: 12, background: `${COLORS.logos}08`, borderRadius: 10, border: `1px solid ${COLORS.logos}15` }}>
              <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 4 }}>{t('tracker.attractorZone')}</div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div>
                  <span style={{ fontSize: 18, fontFamily: fonts.mono, color: COLORS.viability, fontWeight: 700 }}>{analytics.pctAPlus}%</span>
                  <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginLeft: 4 }}>{t('tracker.inAPlus')}</span>
                </div>
                <div>
                  <span style={{ fontSize: 18, fontFamily: fonts.mono, color: COLORS.entropy, fontWeight: 700 }}>{analytics.pctAMinus}%</span>
                  <span style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginLeft: 4 }}>{t('tracker.inAMinus')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PATTERNS TAB ═══ */}
      {subTab === 'patterns' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {/* Day of week heatmap */}
          <div style={{ background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20 }}>
            <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 2, marginBottom: 16, fontWeight: 600 }}>
              {t('tracker.weeklyPattern')}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={analytics.dayAverages} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: COLORS.textMuted, fontFamily: fonts.mono }} />
                <YAxis domain={[0, 1]} tick={{ fontSize: 9, fill: COLORS.textMuted }} />
                <Tooltip contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 10, fontFamily: fonts.mono }} />
                <Bar dataKey="avgLambda" name={t('tracker.lambdaAvgChart')} radius={[4, 4, 0, 0]}>
                  {analytics.dayAverages.map((entry, idx) => (
                    <Cell key={idx} fill={
                      entry.sessions === 0 ? COLORS.border
                        : entry.avgLambda >= 0.45 ? COLORS.viability
                        : entry.avgLambda < 0.20 ? COLORS.entropy
                        : COLORS.freeEnergy
                    } />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8 }}>
              {analytics.dayAverages.map(d => (
                <div key={d.day} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>{d.sessions}s</div>
                </div>
              ))}
            </div>
            {analytics.bestDay && analytics.worstDay && analytics.bestDay.day !== analytics.worstDay.day && (
              <div style={{ marginTop: 12, padding: 10, background: `${COLORS.logos}06`, borderRadius: 8, fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, lineHeight: 1.6 }}>
                📌 {t('tracker.miniBestDay')}: <strong style={{ color: COLORS.viability }}>{analytics.bestDay.day}</strong> (Λ={analytics.bestDay.avgLambda}).
                {t('tracker.miniWorstDay')}: <strong style={{ color: COLORS.entropy }}>{analytics.worstDay.day}</strong> (Λ={analytics.worstDay.avgLambda}).
              </div>
            )}
          </div>

          {/* Lambda distribution / range */}
          <div style={{ background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20 }}>
            <div style={{ fontSize: 11, fontFamily: fonts.mono, color: COLORS.logos, letterSpacing: 2, marginBottom: 16, fontWeight: 600 }}>
              {t('tracker.rangeTitle')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <RangeBar label="Λ Lambda" min={analytics.minLambda} max={analytics.maxLambda} avg={analytics.avgLambda} color={COLORS.logos} />

              <div style={{ marginTop: 8, padding: 12, background: `${COLORS.logos}06`, borderRadius: 8 }}>
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted, marginBottom: 8 }}>{t('tracker.insights')}</div>
                <div style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textDim, lineHeight: 1.8 }}>
                  {analytics.trendDelta > 0.03 && t('tracker.insightUp')}
                  {analytics.trendDelta < -0.03 && t('tracker.insightDown')}
                  {analytics.trendDelta >= -0.03 && analytics.trendDelta <= 0.03 && t('tracker.insightStable')}
                  <br />
                  {analytics.streak >= 7 && t('tracker.streak7plus', { n: analytics.streak })}
                  {analytics.streak >= 3 && analytics.streak < 7 && t('tracker.streak3to6', { n: analytics.streak })}
                  {analytics.streak < 3 && analytics.streak > 0 && t('tracker.streakLow')}
                  {analytics.streak === 0 && t('tracker.streakNone')}
                  <br />
                  {analytics.pctAMinus > 20 && t('tracker.riskAMinus', { pct: analytics.pctAMinus })}
                  {analytics.pctAPlus > 60 && t('tracker.excellentAPlus', { pct: analytics.pctAPlus })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ HELPER COMPONENTS ═══

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{
      textAlign: 'center', padding: '12px 8px',
      background: COLORS.surface, borderRadius: 10,
      border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontFamily: fonts.mono, color, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div style={{ padding: 10, background: `${COLORS.logos}06`, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
      <div style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted, letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 14, fontFamily: fonts.mono, color: COLORS.text, fontWeight: 600, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 9, fontFamily: fonts.mono, color: COLORS.textMuted }}>{sub}</div>}
    </div>
  );
}

function RangeBar({ label, min, max, avg, color }) {
  const pctMin = min * 100;
  const pctMax = max * 100;
  const pctAvg = avg * 100;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 10, fontFamily: fonts.mono, color: COLORS.textMuted }}>{label}</span>
        <span style={{ fontSize: 10, fontFamily: fonts.mono, color }}>avg {avg}</span>
      </div>
      <div style={{ position: 'relative', width: '100%', height: 12, background: COLORS.surface2 || COLORS.border, borderRadius: 6 }}>
        <div style={{
          position: 'absolute', left: `${pctMin}%`, width: `${pctMax - pctMin}%`,
          height: '100%', background: `${color}40`, borderRadius: 6,
        }} />
        <div style={{
          position: 'absolute', left: `${pctAvg}%`, top: -2, width: 3, height: 16,
          background: color, borderRadius: 2, transform: 'translateX(-50%)',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
        <span style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>min {min}</span>
        <span style={{ fontSize: 8, fontFamily: fonts.mono, color: COLORS.textMuted }}>max {max}</span>
      </div>
    </div>
  );
}

function getVerdictColor(verdict) {
  switch (verdict) {
    case 'ACTÚA': return '#22c55e';
    case 'MONITOREA': return '#eab308';
    case 'ESPERA': return '#f97316';
    case 'PAUSA': return '#ef4444';
    case 'RECONECTA': return '#a855f7';
    default: return COLORS.textMuted;
  }
}
